/**
 * Stable Tago payment failure codes.
 *
 * Screens and i18n speak THESE names. A PSP adapter maps its own strings
 * (`AplicativoDebeEstarProductivo`, `idTransaccion`, …) into this table.
 * Adding Stripe is a new adapter, not a new toast in checkout.
 *
 * Edge functions may still emit historical codes (`WOMPI_APP_NOT_PRODUCTIVE`).
 * `PAYMENT_ERROR_KEYS` keeps those aliases so a reasonCode from the server
 * always has copy. `classifyPaymentFailure` folds aliases into the canonical
 * code the UI should switch on.
 */
export const PAYMENT_FAILURE_CODES = [
  'APP_NOT_LIVE',
  'VALIDATION',
  'CHALLENGE_ABANDONED',
  'START_IN_PAST',
  'BOOKING_TOO_SOON',
  'CREATE_FAILED',
  'DECLINED',
  'RENTAL_NO_LONGER_ACTIVE',
  'RENTAL_TOTAL_MISMATCH',
  'COMPLIANCE_INCOMPLETE',
  'SAVED_CARD_DISABLED',
  'HOSTED_CHECKOUT_FAILED',
  'HOSTED_CHECKOUT_DISABLED',
  'UNKNOWN',
] as const

export type PaymentFailureCode = (typeof PAYMENT_FAILURE_CODES)[number]

/**
 * Every code the server has ever put on `code` / `reasonCode`, plus the
 * canonical names above. The validator `validate-payment-reason-codes.mjs`
 * reads this table.
 */
export const PAYMENT_ERROR_KEYS: Record<string, string> = {
  APP_NOT_LIVE: 'payment.app_not_live',
  VALIDATION: 'payment.validation',
  CHALLENGE_ABANDONED: 'payment.challenge_abandoned',
  '3DS_ABANDONED': 'payment.challenge_abandoned',
  START_IN_PAST: 'checkout.start_in_the_past',
  BOOKING_TOO_SOON: 'payment.booking_too_soon',
  CREATE_FAILED: 'payment.could_not_start',
  DECLINED: 'payment.failed_desc',
  RENTAL_NO_LONGER_ACTIVE: 'payment.rental_no_longer_active',
  RENTAL_TOTAL_MISMATCH: 'payment.total_mismatch',
  COMPLIANCE_INCOMPLETE: 'payment.booking_blocked_title',
  SAVED_CARD_DISABLED: 'payment.saved_card_unavailable',
  HOSTED_CHECKOUT_FAILED: 'payment.hosted_checkout_could_not_start',
  HOSTED_CHECKOUT_DISABLED: 'payment.hosted_checkout_could_not_start',
  UNKNOWN: 'payment.failed_desc',

  // Historical edge codes — same copy, old names. Do not add more of these
  // in screens; map new PSP failures through classifyPaymentFailure.
  WOMPI_APP_NOT_PRODUCTIVE: 'payment.app_not_live',
  WOMPI_3DS_CREATE_FAILED: 'payment.bank_verification_unavailable',
  PAYMENT_CREATE_FAILED: 'payment.could_not_start',
  SAVED_CARD_CHARGES_DISABLED: 'payment.saved_card_unavailable',
  PAYPAL_ORDER_CREATE_FAILED: 'payment.hosted_checkout_could_not_start',
  PAYPAL_CHECKOUT_DISABLED: 'payment.hosted_checkout_could_not_start',
  TOURS_REFUND_FAILED: 'tours.errors.refund_failed',
  TOURS_REFUND_RECONCILIATION_REQUIRED: 'tours.errors.refund_reconciliation_required',
}

const CANONICAL_FROM_ALIAS: Record<string, PaymentFailureCode> = {
  APP_NOT_LIVE: 'APP_NOT_LIVE',
  VALIDATION: 'VALIDATION',
  CHALLENGE_ABANDONED: 'CHALLENGE_ABANDONED',
  '3DS_ABANDONED': 'CHALLENGE_ABANDONED',
  START_IN_PAST: 'START_IN_PAST',
  BOOKING_TOO_SOON: 'BOOKING_TOO_SOON',
  CREATE_FAILED: 'CREATE_FAILED',
  DECLINED: 'DECLINED',
  RENTAL_NO_LONGER_ACTIVE: 'RENTAL_NO_LONGER_ACTIVE',
  RENTAL_TOTAL_MISMATCH: 'RENTAL_TOTAL_MISMATCH',
  COMPLIANCE_INCOMPLETE: 'COMPLIANCE_INCOMPLETE',
  SAVED_CARD_DISABLED: 'SAVED_CARD_DISABLED',
  HOSTED_CHECKOUT_FAILED: 'HOSTED_CHECKOUT_FAILED',
  HOSTED_CHECKOUT_DISABLED: 'HOSTED_CHECKOUT_DISABLED',
  UNKNOWN: 'UNKNOWN',
  WOMPI_APP_NOT_PRODUCTIVE: 'APP_NOT_LIVE',
  WOMPI_3DS_CREATE_FAILED: 'CREATE_FAILED',
  PAYMENT_CREATE_FAILED: 'CREATE_FAILED',
  SAVED_CARD_CHARGES_DISABLED: 'SAVED_CARD_DISABLED',
  PAYPAL_ORDER_CREATE_FAILED: 'HOSTED_CHECKOUT_FAILED',
  PAYPAL_CHECKOUT_DISABLED: 'HOSTED_CHECKOUT_DISABLED',
}

export function canonicalizePaymentFailureCode(
  code: string | null | undefined,
): PaymentFailureCode {
  if (!code) return 'UNKNOWN'
  return CANONICAL_FROM_ALIAS[code] ?? 'UNKNOWN'
}

export function paymentFailureI18nKey(code: PaymentFailureCode): string {
  return PAYMENT_ERROR_KEYS[code] ?? 'payment.failed_desc'
}
